/*
Purpose: Custom hook to manage auto-expanding sidebar behavior based on window size.
Audience: Developers.
What it does:
- Detects window size changes and determines when sidebar should auto-expand
- Manages sidebar state for desktop vs mobile experiences
- Provides smooth transitions between manual and auto modes
*/
import { useState, useEffect, useCallback } from 'react';

const useAutoExpandSidebar = (externalState = false) => {
  const [isManuallyOpen, setIsManuallyOpen] = useState(externalState);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  
  // Sync with external state changes
  useEffect(() => {
    setIsManuallyOpen(externalState);
  }, [externalState]);
  
  // Breakpoints for different behaviors
  const MOBILE_BREAKPOINT = 768;
  const AUTO_EXPAND_BREAKPOINT = 1024; // Larger screens where we auto-expand
  
  // Update window width on resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Determine if we're in different screen modes
  const isMobile = windowWidth <= MOBILE_BREAKPOINT;
  const isLargeDesktop = windowWidth >= AUTO_EXPAND_BREAKPOINT;
  const isMediumDesktop = windowWidth > MOBILE_BREAKPOINT && windowWidth < AUTO_EXPAND_BREAKPOINT;
  
  // Calculate if sidebar should be open
  const shouldAutoExpand = isLargeDesktop;
  const isOpen = shouldAutoExpand || isManuallyOpen;
  
  // Handle manual toggle - only affects manual state
  const handleToggle = useCallback((newState) => {
    if (typeof newState === 'boolean') {
      setIsManuallyOpen(newState);
    } else {
      setIsManuallyOpen(current => !current);
    }
  }, []);
  
  // Force close sidebar (useful for mobile navigation)
  const forceClose = useCallback(() => {
    setIsManuallyOpen(false);
  }, []);
  
  // Get sidebar mode for styling purposes
  const getSidebarMode = () => {
    if (isMobile) return 'mobile';
    if (shouldAutoExpand) return 'auto-expanded';
    return 'desktop-manual';
  };
  
  return {
    isOpen,
    isManuallyOpen,
    shouldAutoExpand,
    isMobile,
    isLargeDesktop,
    isMediumDesktop,
    windowWidth,
    sidebarMode: getSidebarMode(),
    handleToggle,
    forceClose
  };
};

export default useAutoExpandSidebar;
